import React, { useState, useEffect, useCallback } from 'react';
import { Activity, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Clock, Calendar } from 'lucide-react';
import { getDailyMovements, getMovementsByDate } from '../api';
import { useStructure } from '../contexts/StructureContext';

const DailyMovements = () => {
  const { currentStructure } = useStructure();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });

  const todayStr = new Date().toISOString().slice(0, 10);
  const isToday = selectedDate === todayStr;

  const fetchMovements = useCallback(async () => {
    if (!currentStructure) return;
    try {
      setLoading(true);
      const response = isToday
        ? await getDailyMovements(currentStructure.id)
        : await getMovementsByDate(currentStructure.id, selectedDate);
      setMovements(response.data);
    } catch (error) {
      console.error('Error fetching movements:', error);
    } finally {
      setLoading(false);
    }
  }, [currentStructure, selectedDate, isToday]);

  useEffect(() => {
    if (currentStructure) {
      fetchMovements();
      // Auto-refresh only for today
      if (isToday) {
        const interval = setInterval(fetchMovements, 2 * 60 * 1000);
        return () => clearInterval(interval);
      }
    }
  }, [currentStructure, fetchMovements, isToday]);

  const navigateDate = (direction) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + direction);
    // Don't go past today
    if (d > new Date()) return;
    setSelectedDate(d.toISOString().slice(0, 10));
  };

  const formatDateLabel = () => {
    if (isToday) return 'Mouvements du jour';
    const d = new Date(selectedDate + 'T12:00:00');
    return `Mouvements du ${d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!currentStructure) return null;

  return (
    <div className="bg-white rounded-lg shadow-md mb-4 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition"
      >
        <div className="flex items-center gap-2">
          <Activity size={18} className="text-primary" />
          <span className="font-medium text-gray-700 text-sm">{formatDateLabel()}</span>
          {movements.length > 0 && (
            <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
              {movements.length}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {/* Date navigation */}
          <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100">
            <button
              onClick={(e) => { e.stopPropagation(); navigateDate(-1); }}
              className="p-1 hover:bg-gray-200 rounded transition"
              title="Jour précédent"
            >
              <ChevronLeft size={18} className="text-gray-600" />
            </button>
            <div className="relative">
              <input
                type="date"
                value={selectedDate}
                max={todayStr}
                onChange={(e) => setSelectedDate(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="text-sm text-gray-700 bg-white border border-gray-200 rounded px-2 py-1 cursor-pointer"
              />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); navigateDate(1); }}
              className={`p-1 rounded transition ${isToday ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200 text-gray-600'}`}
              disabled={isToday}
              title="Jour suivant"
            >
              <ChevronRight size={18} className="text-gray-600" />
            </button>
            {!isToday && (
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedDate(todayStr); }}
                className="text-xs text-primary hover:text-primary-dark font-medium ml-1"
              >
                Aujourd'hui
              </button>
            )}
          </div>

          {/* Movements list */}
          <div className="max-h-64 overflow-y-auto">
            {loading && movements.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">Chargement...</div>
            ) : movements.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                Aucun mouvement {isToday ? "aujourd'hui" : 'ce jour'}
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {movements.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                    <div className="flex-shrink-0">
                      {m.amount > 0 ? (
                        <TrendingUp size={16} className="text-green-500" />
                      ) : (
                        <TrendingDown size={16} className="text-red-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-800 truncate block">
                        {m.product?.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {m.product?.category?.name}
                      </span>
                    </div>
                    <div className={`font-semibold text-sm ${m.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {m.amount > 0 ? '+' : ''}{m.amount}
                    </div>
                    <div className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                      <Clock size={12} />
                      {formatTime(m.timestamp)}
                    </div>
                    {(m.person || m.personInitials) && (
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center" title={m.person ? `${m.person.firstName} ${m.person.lastName}` : m.personName}>
                        {m.person?.initials || m.personInitials || '?'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyMovements;
